import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

/**
 * Format any error returned by the backend or thrown by axios into a single,
 * human-readable string. Surfaces real causes instead of "Something went wrong".
 */
export function formatApiError(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (typeof e === "string") return e;
        if (e && typeof e.msg === "string") {
          const loc = Array.isArray(e.loc) ? e.loc.filter((p) => p !== "body").join(" → ") : "";
          return loc ? `${loc}: ${e.msg}` : e.msg;
        }
        return JSON.stringify(e);
      })
      .filter(Boolean)
      .join(" · ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return JSON.stringify(detail);
}

/**
 * Read an entire axios error and produce a single friendly message.
 * Use this in catch blocks instead of `formatApiError(err.response?.data?.detail)`.
 */
export function describeAxiosError(err) {
  if (!err) return "Unknown error.";

  // Network / no response
  if (!err.response) {
    if (err.code === "ECONNABORTED" || /timeout/i.test(err.message)) {
      return "The request timed out. Please check your connection and try again.";
    }
    if (err.code === "ERR_NETWORK" || /Network Error/i.test(err.message)) {
      return "Network connection lost. Please check your internet and try again.";
    }
    return err.message || "Could not reach the server.";
  }

  const status = err.response.status;
  const data = err.response.data || {};
  const detailMsg = formatApiError(data.detail);

  if (detailMsg) return detailMsg;

  switch (status) {
    case 400: return "Some of the information you submitted is invalid. Please review and try again.";
    case 401: return "You need to sign in to do that.";
    case 403: return "You don't have permission to do that.";
    case 404: return "The requested resource was not found.";
    case 409: return "That already exists.";
    case 422: return "Some required fields are missing or invalid.";
    case 429: return "Too many requests. Please wait a moment and try again.";
    case 500: return "Our server hit a problem. We've been notified — please try again in a minute.";
    case 502:
    case 503:
    case 504: return "Service is temporarily unavailable. Please try again shortly.";
    default: return `Request failed (HTTP ${status}). Please try again.`;
  }
}
